/**
 * One person's permissions, when their role's are not the right answer.
 *
 * Two states, and the difference matters: an account either follows its role,
 * picking up changes to that role as they happen, or it is pinned to an exact
 * list and stops following. The screen says which it is doing, because from
 * the ticks alone the two look identical on the day they are set.
 *
 * The catalogues come from the server, and anything stored that this cannot
 * show is put back on save, for the same reason as the role grid.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Checkbox, Group, Loader, Modal, ScrollArea, SimpleGrid, Stack, Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as gw from '../../api/gateway';
import { useEnums } from '../../api/queries';
import { roleLabel, useVocab } from '../../vocab';
import { permissionLabel } from './PermissionsTab';

export function CustomiseUser({
  user,
  onClose,
}: {
  user: gw.GatewayAccount | null;
  onClose: () => void;
}) {
  const v = useVocab();
  const qc = useQueryClient();
  const enums = useEnums();
  const perms = useQuery({
    queryKey: ['user-permissions', user?.id],
    queryFn: () => gw.userPermissions(user!.id),
    enabled: !!user,
  });

  const [actions, setActions] = useState<string[]>([]);
  const [navTabs, setNavTabs] = useState<string[]>([]);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!perms.data) return;
    setActions(perms.data.actions);
    setNavTabs(perms.data.nav_tabs);
    setTouched(false);
  }, [perms.data]);

  const allActions = enums.data?.actions ?? [];
  const allTabs = enums.data?.nav_tabs ?? [];

  /** Keep anything stored that this screen has no checkbox for. */
  const withHidden = (edited: string[], held: string[], catalogue: string[]) => [
    ...edited,
    ...held.filter((k) => !catalogue.includes(k)),
  ];

  const save = useMutation({
    mutationFn: () =>
      gw.setUserPermissions(user!.id, {
        actions: withHidden(actions, perms.data?.actions ?? [], allActions),
        nav_tabs: withHidden(navTabs, perms.data?.nav_tabs ?? [], allTabs),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-permissions', user?.id] });
      onClose();
      notifications.show({
        color: 'teal',
        title: 'Saved',
        message: 'They now keep exactly these, whatever their role changes to.',
      });
    },
    onError: (e) =>
      notifications.show({ color: 'red', title: 'Could not save', message: (e as Error).message }),
  });

  const reset = useMutation({
    mutationFn: () => gw.resetUserPermissions(user!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-permissions', user?.id] });
      onClose();
      notifications.show({
        color: 'teal',
        title: 'Back to their role',
        message: 'They follow their role again, including any later changes to it.',
      });
    },
    onError: (e) =>
      notifications.show({ color: 'red', title: 'Could not reset', message: (e as Error).message }),
  });

  const toggle = (held: string[], set: (v: string[]) => void, key: string) => {
    set(held.includes(key) ? held.filter((k) => k !== key) : [...held, key]);
    setTouched(true);
  };

  const overridden = perms.data?.has_override ?? false;

  return (
    <Modal opened={!!user} onClose={onClose} title={`Permissions for ${user?.email ?? ''}`} size="lg">
      {perms.isPending || enums.isPending ? (
        <Loader size="sm" />
      ) : (
        <Stack>
          <Group gap="xs">
            <Badge variant="light" color="gray">{roleLabel(v, perms.data?.role)}</Badge>
            <Badge variant="light" color={overridden ? 'chartAmber' : 'teal'}>
              {overridden ? 'Set for this person' : 'Following their role'}
            </Badge>
          </Group>

          <Alert color="gray" variant="light">
            <Text size="sm">
              {overridden
                ? 'These are set for this person alone. Changing what the role can do will not change them until you reset this.'
                : 'These come from their role, and change whenever the role does. Saving here pins them to this person, and they stop following the role.'}
            </Text>
          </Alert>

          <ScrollArea.Autosize mah={420}>
            <Stack gap="lg">
              <Section
                title="What they can do"
                keys={allActions}
                held={actions}
                onToggle={(k) => toggle(actions, setActions, k)}
              />
              <Section
                title="What they can see"
                keys={allTabs}
                held={navTabs}
                onToggle={(k) => toggle(navTabs, setNavTabs, k)}
              />
            </Stack>
          </ScrollArea.Autosize>

          <Group justify="space-between">
            <Button
              variant="subtle"
              color="gray"
              disabled={!overridden}
              loading={reset.isPending}
              onClick={() => reset.mutate()}
            >
              Follow their role again
            </Button>
            <Group>
              <Button variant="subtle" onClick={onClose}>Cancel</Button>
              <Button loading={save.isPending} disabled={!touched} onClick={() => save.mutate()}>
                Save for this person
              </Button>
            </Group>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

function Section({
  title,
  keys,
  held,
  onToggle,
}: {
  title: string;
  keys: string[];
  held: string[];
  onToggle: (key: string) => void;
}) {
  const v = useVocab();
  return (
    <Stack gap="xs">
      <Text fw={600} size="sm">{title}</Text>
      <SimpleGrid cols={2} spacing="xs">
        {keys.map((k) => (
          <Checkbox
            key={k}
            label={permissionLabel(v, k)}
            checked={held.includes(k)}
            onChange={() => onToggle(k)}
          />
        ))}
      </SimpleGrid>
    </Stack>
  );
}
